import moment from "moment";
import type { DemoLead } from "./demoOps.lib";

export type LiveDemoStatusFilter =
  | ""
  | "SCHEDULED"
  | "RESCHEDULED"
  | "ONGOING"
  | "COMPLETED"
  | "CANCELLED"
  | "NOT CONDUCTED"
  | "NOSHOW";

export type LiveDemoAeFilter = "" | "NO_AE" | "AE_FOUND";

export interface LiveDemoFilters {
  country: string;
  status: LiveDemoStatusFilter;
  aeFound: LiveDemoAeFilter;
  timezone: string;
}

export const LIVE_DEMO_STATUS_OPTIONS: { value: LiveDemoStatusFilter; label: string }[] = [
  { value: "", label: "All statuses" },
  { value: "SCHEDULED", label: "Scheduled" },
  { value: "RESCHEDULED", label: "Rescheduled" },
  { value: "ONGOING", label: "Ongoing" },
  { value: "COMPLETED", label: "Completed" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "NOT CONDUCTED", label: "Not conducted" },
  { value: "NOSHOW", label: "No show" },
];

const HINDI_DEMO_COURSES = [
  "Western Vocal",
  "Western Vocals",
  "Popular & Film Music - Hindi",
  "Hindi Devotional",
  "Hindustani Classical Vocal",
  "Karaoke - Hindi",
  "Ghazal",
];

const PIANO_KEYBOARD_COURSES = [
  "Popular & Film Music - Tamil",
  "Popular & Film Music - Kannada",
  "Popular & Film Music - Malayalam",
  "Popular & Film Music - Telugu",
  "Carnatic Classical Vocal",
  "Keyboard & Piano",
  "Guitar",
];

/** Asia/Kolkata or Asia/Calcutta (common India timezone aliases). */
export function isAsiaKolkataTimezone(lead: DemoLead): boolean {
  const tz = String(lead.studentTimezone ?? "")
    .trim()
    .toLowerCase()
    .replace(/\\/g, "/");
  return tz === "asia/kolkata" || tz === "asia/calcutta";
}

/** True when the lead's phone is an India (+91) number. */
export function isIndiaCountryCode(lead: DemoLead): boolean {
  const code = String(lead.countryCode ?? "").trim();
  if (code === "+91" || code === "91") return true;
  const combined = `${lead.countryCode ?? ""}${lead.mobileNumber ?? ""}`.replace(
    /\s+/g,
    ""
  );
  return combined.startsWith("+91");
}

/**
 * Live Demo domestic highlight:
 * Asia/Kolkata (or Asia/Calcutta) AND number is +91.
 * This file only highlights — it does not remove leads.
 */
export function isDomesticIndiaLead(lead: DemoLead): boolean {
  return isAsiaKolkataTimezone(lead) && isIndiaCountryCode(lead);
}

export function shouldShowDintCallRow(lead: DemoLead): boolean {
  if (!isDomesticIndiaLead(lead)) return false;
  const courseName = String(lead.courseName ?? "").trim();
  return HINDI_DEMO_COURSES.some((c) => c.trim() === courseName);
}

export function isPianoKeyboardRow(lead: DemoLead): boolean {
  if (!isDomesticIndiaLead(lead)) return false;
  return PIANO_KEYBOARD_COURSES.includes(String(lead.courseName ?? ""));
}

function matchesStatusFilter(lead: DemoLead, status: LiveDemoStatusFilter): boolean {
  if (!status) return true;

  const demoStatus = String(lead.demoStatus ?? "");
  const actionPerformed = String(lead.actionPerformed ?? "");
  const meetType = String(lead.meetType ?? "");
  const studentJoined = !!lead.studentJoinTime;
  const teacherJoined = !!lead.teacherJoinTime;

  switch (status) {
    case "SCHEDULED":
      return (
        demoStatus === "SCHEDULED" &&
        ((studentJoined && !teacherJoined) ||
          (!studentJoined && teacherJoined) ||
          (!studentJoined && !teacherJoined))
      );
    case "RESCHEDULED":
      return demoStatus === "Class_Ready_For_Scheduling" && !!lead.classDeleted;
    case "ONGOING":
      return (
        demoStatus !== "COMPLETED" &&
        studentJoined &&
        teacherJoined &&
        !actionPerformed &&
        meetType !== "deleted" &&
        !lead.classDeleted
      );
    case "COMPLETED":
      return demoStatus === "COMPLETED";
    case "CANCELLED":
      return (
        actionPerformed === "student_trial_cancelclass" &&
        demoStatus !== "Class_Ready_For_Scheduling"
      );
    case "NOT CONDUCTED":
      return (
        actionPerformed === "teacher_demo_cancelclass" &&
        demoStatus !== "Class_Ready_For_Scheduling"
      );
    case "NOSHOW":
      return meetType.includes("NoShow");
    default:
      return true;
  }
}

export type LiveDemoSortDir = "asc" | "desc";

export function filterLiveDemoLeads(
  leads: DemoLead[],
  filters: LiveDemoFilters,
  sortDir: LiveDemoSortDir = "desc"
): DemoLead[] {
  let filtered = [...leads];

  if (filters.country) {
    filtered = filtered.filter((demo) => demo.country === filters.country);
  }

  if (filters.status) {
    filtered = filtered.filter((demo) => matchesStatusFilter(demo, filters.status));
  }

  if (filters.aeFound) {
    filtered =
      filters.aeFound === "NO_AE"
        ? filtered.filter((demo) => !demo.teacherFirstName)
        : filtered.filter((demo) => !!demo.teacherFirstName);
  }

  if (filters.timezone) {
    const tz = filters.timezone.trim();
    filtered = filtered.filter(
      (demo) => demo.studentTimezone && String(demo.studentTimezone).trim() === tz
    );
  }

  // Live Demo only: demo time (asc/desc), then status priority, then a stable id tie-break
  const dir = sortDir === "desc" ? -1 : 1;
  return filtered.sort(
    (a, b) =>
      dir * String(a.startTime ?? "").localeCompare(String(b.startTime ?? "")) ||
      liveDemoStatusRank(a) - liveDemoStatusRank(b) ||
      Number(a.userId ?? 0) - Number(b.userId ?? 0) ||
      Number(a.recordId ?? 0) - Number(b.recordId ?? 0)
  );
}

const LIVE_DEMO_STATUS_RANK: Record<string, number> = {
  ONGOING: 0,
  SCHEDULED: 1,
  RESCHEDULED: 2,
  COMPLETED: 3,
  CANCELLED: 4,
  "NOT CONDUCTED": 5,
  NOSHOW: 6,
};

function liveDemoStatusRank(lead: DemoLead): number {
  return LIVE_DEMO_STATUS_RANK[getLiveDemoStatusBadge(lead).label] ?? 9;
}

export function formatDemoStartTime(startTime?: string): string {
  if (!startTime) return "N/A";
  const [hours, minutes] = startTime.split(":");
  let hoursInt = parseInt(hours, 10);
  if (Number.isNaN(hoursInt)) return startTime;
  const ampm = hoursInt >= 12 ? "PM" : "AM";
  hoursInt = hoursInt % 12;
  hoursInt = hoursInt || 12;
  return `${hoursInt}:${minutes ?? "00"} ${ampm}`;
}

function classStartToday(startTime?: string): moment.Moment | null {
  if (!startTime) return null;
  const m = moment(`${moment().format("YYYY-MM-DD")} ${startTime}`, "YYYY-MM-DD HH:mm:ss");
  if (m.isValid()) return m;
  return moment(`${moment().format("YYYY-MM-DD")} ${startTime}`, "YYYY-MM-DD HH:mm");
}

export function isClassStartInFuture(lead: DemoLead): boolean {
  const start = classStartToday(lead.startTime);
  return start ? start.isAfter(moment()) : false;
}

export function calculateElapsedTime(latestJoinTime?: string): string | null {
  if (!latestJoinTime) return null;
  const now = moment();
  const join = moment(latestJoinTime);
  if (!join.isValid()) return null;
  const diff = now.diff(join, "second");
  const minutes = Math.floor(diff / 60);
  const seconds = diff % 60;
  const mm = minutes < 10 ? `0${minutes}` : `${minutes}`;
  const ss = seconds < 10 ? `0${seconds}` : `${seconds}`;
  return `${mm}m ${ss}s`;
}

export type LiveDemoBadge = {
  label: string;
  color: string;
  elapsed?: string | null;
  tall?: boolean;
};

export function getLiveDemoStatusBadge(lead: DemoLead): LiveDemoBadge {
  const demoStatus = String(lead.demoStatus ?? "");
  const actionPerformed = String(lead.actionPerformed ?? "");
  const meetType = String(lead.meetType ?? "");
  const studentJoined = !!lead.studentJoinTime;
  const teacherJoined = !!lead.teacherJoinTime;

  if (
    studentJoined &&
    teacherJoined &&
    demoStatus !== "COMPLETED" &&
    !actionPerformed &&
    meetType !== "deleted" &&
    !lead.classDeleted
  ) {
    const studentJoin = moment(lead.studentJoinTime);
    const teacherJoin = moment(lead.teacherJoinTime);
    const latestJoinTime =
      studentJoin.isValid() && teacherJoin.isValid()
        ? moment.max(studentJoin, teacherJoin)
        : studentJoin.isValid()
          ? studentJoin
          : teacherJoin;
    return {
      label: "ONGOING",
      color: "#b2c888",
      elapsed: calculateElapsedTime(latestJoinTime.toISOString()),
      tall: true,
    };
  }

  if (meetType.includes("NoShow")) {
    return { label: "NOSHOW", color: "#fa5775" };
  }

  if (demoStatus === "COMPLETED") {
    return { label: "COMPLETED", color: "#4caf50" };
  }

  if (demoStatus === "Class_Ready_For_Scheduling") {
    return { label: "RESCHEDULED", color: "lightblue" };
  }

  if (demoStatus === "SCHEDULED") {
    if (actionPerformed === "teacher_demo_cancelclass") {
      return { label: "NOT CONDUCTED", color: "#fcaefc" };
    }
    if (actionPerformed === "student_trial_cancelclass") {
      return { label: "CANCELLED", color: "#fa9237" };
    }
  }

  if (demoStatus === "AE not found") {
    return { label: "AE not found", color: "red" };
  }

  if (demoStatus === "Student No Show") {
    return { label: "Student No Show", color: "orange" };
  }

  return { label: demoStatus || "—", color: "#fbcc68" };
}

export function formatStudentJoined(lead: DemoLead): string {
  if (isClassStartInFuture(lead)) return "—";
  if (!lead.studentJoinTime) return "Student has not joined";
  const m = moment(lead.studentJoinTime, "YYYY-MM-DD HH:mm:ss");
  return m.isValid() ? m.format("hh:mm A") : String(lead.studentJoinTime);
}

export function formatAeJoined(lead: DemoLead): {
  kind: "future" | "not_found" | "not_joined" | "joined" | "late";
  time?: string;
} {
  if (`${lead.classStatus ?? ""}`.includes("Found")) {
    return { kind: "not_found" };
  }
  if (isClassStartInFuture(lead)) {
    return { kind: "future" };
  }
  if (!lead.teacherJoinTime) {
    return { kind: "not_joined" };
  }
  const studentJoin = moment(lead.studentJoinTime);
  const teacherJoin = moment(lead.teacherJoinTime);
  const diffInMinutes = teacherJoin.diff(studentJoin, "minute");
  const time = teacherJoin.format("hh:mm A");
  if (diffInMinutes >= 2) {
    return { kind: "late", time };
  }
  return { kind: "joined", time };
}

export function aeDisplayName(lead: DemoLead): string {
  const name = String(lead.teacherFirstName ?? "").trim();
  if (!name || name === "Academic Expert") return "—";
  return name;
}
